import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    exhaustMap,
    filter,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    withLatestFrom,
} from "rxjs";
import {
    ManagedWorkflow,
    WorkflowStep,
    WorkflowStepDraft,
} from "../models/workflow.models";
import { WorkflowsApiService } from "../data-access/workflows-api.service";
import { WorkflowsActions } from "./workflows.actions";
import { workflowsFeature } from "./workflows.reducer";

@Injectable()
export class WorkflowsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(WorkflowsApiService);

    readonly open$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.pageOpened),
            switchMap(() =>
                this.api.loadWorkflows().pipe(
                    map((workflows) =>
                        WorkflowsActions.workflowsLoaded({ workflows })
                    ),
                    catchError(() => of(WorkflowsActions.workflowsLoadFailed()))
                )
            )
        )
    );

    readonly selectAfterLoad$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.workflowsLoaded),
            withLatestFrom(
                this.store.select(workflowsFeature.selectSelectedWorkflowId)
            ),
            filter(([, workflowId]) => Boolean(workflowId)),
            map(([, workflowId]) =>
                WorkflowsActions.workflowSelected({ workflowId: workflowId! })
            )
        )
    );

    readonly loadSteps$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.workflowSelected),
            withLatestFrom(this.store.select(workflowsFeature.selectWorkflows)),
            switchMap(([{ workflowId }, workflows]) => {
                const workflow = workflows.find(
                    (current) => current.gmvid === workflowId
                );
                if (!workflow?.steps.length) {
                    return of(
                        WorkflowsActions.stepsLoaded({ workflowId, steps: [] })
                    );
                }
                return forkJoin(
                    workflow.steps.map((stepId) =>
                        this.api
                            .loadStep(stepId)
                            .pipe(catchError(() => of(null)))
                    )
                ).pipe(
                    map((steps) =>
                        WorkflowsActions.stepsLoaded({
                            workflowId,
                            steps: steps.filter((step): step is WorkflowStep =>
                                Boolean(step)
                            ),
                        })
                    ),
                    catchError(() =>
                        of(WorkflowsActions.stepsLoadFailed({ workflowId }))
                    )
                );
            })
        )
    );

    readonly create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.createRequested),
            exhaustMap(({ title }) =>
                this.api.createWorkflow(title).pipe(
                    map((workflow) =>
                        WorkflowsActions.createSucceeded({ workflow })
                    ),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                )
            )
        )
    );

    readonly selectCreated$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.createSucceeded),
            map(({ workflow }) =>
                WorkflowsActions.workflowSelected({
                    workflowId: workflow.gmvid,
                })
            )
        )
    );

    readonly rename$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.renameRequested),
            exhaustMap(({ workflow, title }) =>
                this.api.updateWorkflow({ ...workflow, title }).pipe(
                    map((updated) =>
                        WorkflowsActions.renameSucceeded({ workflow: updated })
                    ),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                )
            )
        )
    );

    readonly delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.deleteRequested),
            exhaustMap(({ workflowId }) =>
                this.api.deleteWorkflow(workflowId).pipe(
                    map(() => WorkflowsActions.deleteSucceeded({ workflowId })),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                )
            )
        )
    );

    readonly updateOwner$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.ownerUpdateRequested),
            exhaustMap(({ workflow, owner }) => {
                const updated = {
                    ...workflow,
                    owner: owner?.username ?? null,
                    ownerInfo: owner,
                };
                return this.api.updateWorkflow(updated).pipe(
                    map((saved) =>
                        WorkflowsActions.ownerUpdateSucceeded({
                            workflow: saved,
                        })
                    ),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                );
            })
        )
    );

    readonly selectAfterDelete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.deleteSucceeded),
            withLatestFrom(
                this.store.select(workflowsFeature.selectSelectedWorkflowId)
            ),
            filter(([, workflowId]) => Boolean(workflowId)),
            map(([, workflowId]) =>
                WorkflowsActions.workflowSelected({ workflowId: workflowId! })
            )
        )
    );

    readonly createStep$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.createStepRequested),
            exhaustMap(({ workflow, draft }) =>
                this.mediaUrl(workflow, draft).pipe(
                    switchMap((pictureURL) =>
                        this.api.createStep(workflow, draft, pictureURL)
                    ),
                    switchMap((step) => {
                        const updated = {
                            ...workflow,
                            steps: [...workflow.steps, step.gmvid],
                        };
                        return this.api.updateWorkflow(updated).pipe(
                            map((saved) =>
                                WorkflowsActions.createStepSucceeded({
                                    workflow: saved,
                                    step,
                                })
                            )
                        );
                    }),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                )
            )
        )
    );

    readonly updateStep$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.updateStepRequested),
            exhaustMap(({ workflow, step, draft }) =>
                this.mediaUrl(workflow, draft).pipe(
                    switchMap((pictureURL) =>
                        this.api.updateStep(step, draft, pictureURL)
                    ),
                    switchMap((updatedStep) => {
                        const updatedWorkflow = {
                            ...workflow,
                            steps: workflow.steps.map((stepId) =>
                                stepId === step.gmvid
                                    ? updatedStep.gmvid
                                    : stepId
                            ),
                        };
                        const saveWorkflow$ =
                            updatedStep.gmvid === step.gmvid
                                ? of(updatedWorkflow)
                                : this.api.updateWorkflow(updatedWorkflow);
                        return saveWorkflow$.pipe(
                            map((saved) =>
                                WorkflowsActions.updateStepSucceeded({
                                    workflow: saved,
                                    step: updatedStep,
                                    originalStepId: step.gmvid,
                                })
                            )
                        );
                    }),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                )
            )
        )
    );

    readonly deleteStep$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.deleteStepRequested),
            exhaustMap(({ workflow, stepId }) => {
                const updated = {
                    ...workflow,
                    steps: workflow.steps.filter((id) => id !== stepId),
                };
                return this.api.updateWorkflow(updated).pipe(
                    map((saved) =>
                        WorkflowsActions.deleteStepSucceeded({
                            workflow: saved,
                            stepId,
                        })
                    ),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                );
            })
        )
    );

    readonly reorderSteps$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WorkflowsActions.reorderStepsRequested),
            exhaustMap(({ workflow, previousIndex, currentIndex }) => {
                const steps = [...workflow.steps];
                const [moved] = steps.splice(previousIndex, 1);
                if (!moved) return of(WorkflowsActions.mutationFailed());
                steps.splice(currentIndex, 0, moved);
                return this.api.updateWorkflow({ ...workflow, steps }).pipe(
                    map((saved) =>
                        WorkflowsActions.reorderStepsSucceeded({
                            workflow: saved,
                        })
                    ),
                    catchError(() => of(WorkflowsActions.mutationFailed()))
                );
            })
        )
    );

    private mediaUrl(
        workflow: ManagedWorkflow,
        draft: WorkflowStepDraft
    ): Observable<string> {
        return draft.file
            ? this.api.uploadWorkflowMedia(draft.file, workflow.gmvid)
            : of(draft.existingPictureUrl);
    }
}
